import { createIdbPersister } from '@/lib/QueryPersisterIdb.js'
import {
  bulkUpdate,
  getTypes,
  getUniqueValues,
  addOrUpdateField
} from '@/services/custom-fields-api.service.js'
import {
  orgChartSortValuesBoard,
  personCustomFields,
  personData,
  uniqueCustomFieldsValues
} from '@/services/queryKeys.service'
import { useMutation, useQuery, useQueryClient } from '@tanstack/vue-query'
import { computed, unref } from 'vue'
import { useStore } from 'vuex'

export function useCustomFields(boardId) {
  const persister = createIdbPersister()
  const queryClient = useQueryClient()
  const { getters } = useStore()

  const uid = computed(() => getters.uid)

  // Always use the original board ID for types (main board for scenarios)
  const originalBoardId = computed(() => {
    const board = getters.getBoard(unref(boardId))
    return board?.originalBoardId || unref(boardId)
  })

  const typesQueryKey = [
    'custom-fields-types',
    { uid: unref(uid), boardId: unref(originalBoardId) }
  ]
  const {
    data: types,
    isPending: isLoadingTypes,
    refetch: refetchTypes
  } = useQuery({
    queryKey: typesQueryKey,
    queryFn: () => getTypes({ boardId: unref(originalBoardId) }),
    persister
  })

  const uniqueValuesQueryKey = computed(() => uniqueCustomFieldsValues(boardId, uid))

  const { data: uniqueValues, isPending: isLoadingUniqueValues } = useQuery({
    queryKey: uniqueValuesQueryKey,
    queryFn: () =>
      getUniqueValues({
        boardId: unref(boardId)
      }),
    persister
  })

  const { mutateAsync: mutateTypes } = useMutation({
    mutationFn: ({ fields }) => addOrUpdateField({ boardId: unref(originalBoardId), fields }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: typesQueryKey })
    }
  })

  const { mutateAsync: mutateValuesBulk } = useMutation({
    mutationFn: ({ peopleIds, values, historyMeta = null }) =>
      bulkUpdate({ boardId: unref(boardId), peopleIds, values, historyMeta }),
    onSuccess: (data, variables) => {
      queryClient.invalidateQueries({ queryKey: uniqueValuesQueryKey })
      variables.peopleIds.forEach((personId) => {
        queryClient.invalidateQueries({ queryKey: personCustomFields(boardId, personId) })
        queryClient.invalidateQueries({ queryKey: personData(boardId, personId) })
      })
      queryClient.invalidateQueries({
        queryKey: ['scenario-history', { boardId: unref(boardId) }]
      })
      queryClient.invalidateQueries({
        queryKey: ['scenario-history-pagination', { boardId: unref(boardId) }]
      })
      queryClient.invalidateQueries({
        queryKey: ['scenario-history-group-detail', { boardId: unref(boardId) }]
      })
      queryClient.invalidateQueries({
        queryKey: orgChartSortValuesBoard(boardId)
      })
    }
  })

  async function bulkUpdateValues({ peopleIds, values, historyMeta = null }) {
    return mutateValuesBulk({ peopleIds, values, historyMeta })
  }

  const handleTypesRefetch = async () => {
    await refetchTypes()
  }

  return {
    types,
    uniqueValues,
    isLoadingTypes,
    isLoadingUniqueValues,
    bulkUpdateValues,
    handleTypesRefetch,
    updateTypes: mutateTypes
  }
}

export function useCustomFieldsHelper(boardId) {
  const queryClient = useQueryClient()

  /**
   * Invalidate per-person custom field values for the entire board.
   * Use after enrichment or bulk data updates that change custom field data.
   */
  async function invalidateValues() {
    await queryClient.invalidateQueries({
      queryKey: ['custom-fields-values', { boardId: unref(boardId) }]
    })
  }

  /**
   * Invalidate custom field types and unique values — these two are usually
   * invalidated together as they represent field definitions and filter options.
   */
  async function invalidateMetadata() {
    await Promise.allSettled([
      queryClient.invalidateQueries({
        queryKey: ['custom-fields-values-unique', { boardId: unref(boardId) }]
      }),
      queryClient.invalidateQueries({
        queryKey: ['custom-fields-types', { boardId: unref(boardId) }]
      })
    ])
  }

  return {
    invalidateValues,
    invalidateMetadata
  }
}
